/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Modal, message, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DbAttachment, DbCommit, DbFile, DbFolder } from '~src/types/db';
import { restoreAttachment } from '~src/services/api';
import { appLog } from '~src/utils/log';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import { useTenderId } from '~src/hooks/useTenderId';

export interface RestoreAttachmentsProps extends Partial<ModalProps> {
  attachments?: DbAttachment[];
  onDone?: () => void;
}
export const RestoreAttachments: React.FC<RestoreAttachmentsProps> = ({
  attachments = [],
  onCancel,
  onDone,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();
  const tenderId = useTenderId();

  const [doRestoreAll, { isLoading }] = useMutation(restoreAll, {
    onSuccess: async () => {
      // hide modal
      // @ts-ignore
      onDone ? onDone() : onCancel && onCancel();

      if (attachments.length) {
        queryCache.invalidateQueries('tendersTenderAttachments');
      }
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'restore-failed' }));
      appLog('RestoreAttachments -> restoreAll -> items', item);
      appLog('RestoreAttachments -> restoreAll -> err', error);
    },
  });

  if (modalProps.visible && !attachments.length)
    throw new Error(
      'RestoreAttachments should be shown with files and/or folders!'
    );

  const handleSubmit = async () => {
    doRestoreAll({ attachments, tenderId });
  };

  let singleEntity: DbFile | DbFolder | DbAttachment | undefined;
  if (attachments.length) {
    singleEntity = attachments[0];
  }

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'restore' })}
          icon={['far', 'trash-restore']}
        />
      }
      okText={intl.formatMessage({ id: 'restore' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ loading: isLoading }}
      onOk={handleSubmit}
      onCancel={onCancel}
      {...modalProps}>
      <Typography.Paragraph css={styles.text}>
        {intl.formatMessage({ id: 'restore' })}?
      </Typography.Paragraph>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  text: {
    textAlign: 'center',
    margin: 0,
    fontSize: 15,
  },
});

interface DeleteAllParams {
  attachments?: DbAttachment[];
  tenderId?: number;
}
const restoreAll = async ({
  attachments = [],
  tenderId,
}: DeleteAllParams): Promise<Array<DbAttachment>> => {
  const attachmentsPromises = attachments.map((attachment) =>
    restoreAttachment({
      projectId: String(attachment.project_id),
      attachment,
      tenderId: String(tenderId),
    })
  );

  return Promise.all([...attachmentsPromises] as Promise<DbAttachment>[]);
};
